/**
 * ---------------------------------------------------------------------------------------------------------------------
 * CUSTOM SECTIONS — shared behaviour
 *
 * Reveal on scroll for the custom sections. The theme ships its own "[reveal]" mechanism but it is wired to a handful of
 * specific custom elements only, so custom sections get their own — deliberately "progressive": nothing is hidden until
 * the script has run, which means the content stays visible if JavaScript fails or is disabled.
 * ---------------------------------------------------------------------------------------------------------------------
 */
(() => {
  const GROUP_SELECTOR = '[data-cn-reveal]';
  const ITEM_SELECTOR = '[data-cn-reveal-item]';

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const reveal = (group) => {
    group.classList.add('cn-reveal--visible');
  };

  const setupGroup = (group) => {
    if (group.dataset.cnRevealReady === 'true') {
      return;
    }

    group.dataset.cnRevealReady = 'true';

    const items = group.matches(ITEM_SELECTOR) ? [group] : Array.from(group.querySelectorAll(ITEM_SELECTOR));

    // The hidden state comes from the stylesheet, so a group with nothing to animate must be revealed straight away
    // or it would stay invisible forever.
    if (items.length === 0 || prefersReducedMotion() || !('IntersectionObserver' in window)) {
      reveal(group);
      return;
    }

    items.forEach((item, index) => item.style.setProperty('--cn-reveal-index', index));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0 }
    );

    observer.observe(group);
  };

  const setupAll = (root = document) => {
    root.querySelectorAll(GROUP_SELECTOR).forEach(setupGroup);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setupAll());
  } else {
    setupAll();
  }

  // Theme editor: a section that gets re-rendered must be re-observed.
  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));

  // In the theme editor, reveal immediately so merchants never edit an invisible block.
  document.addEventListener('shopify:section:select', (event) => {
    event.target.querySelectorAll(GROUP_SELECTOR).forEach(reveal);
  });

  document.addEventListener('shopify:block:select', (event) => {
    event.target.closest(GROUP_SELECTOR)?.classList.add('cn-reveal--visible');
  });
})();

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * Header dropdown alignment.
 *
 * A dropdown opens from the LEFT edge of its menu item, the same as everywhere else in the theme. Because the split
 * header puts a second menu right against the edge of the container, some of those dropdowns would run off the screen —
 * so each one is measured and flipped to hang from the RIGHT edge of its item only when it does not fit. On a wide
 * screen there is room to spare and every dropdown stays left aligned; the flip is re-evaluated on resize.
 *
 * Mega menus are skipped: they are full width and positioned against the section, not against their menu item.
 * ---------------------------------------------------------------------------------------------------------------------
 */
(() => {
  const ITEM_SELECTOR = '.cn-header__nav .header__linklist-item.has-dropdown';
  const EDGE_MARGIN = 16;

  const alignDropdowns = () => {
    // clientWidth excludes the scrollbar, which is exactly the space a dropdown has to fit into.
    const viewportWidth = document.documentElement.clientWidth;

    document.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
      const dropdown = item.querySelector(':scope > .nav-dropdown');

      if (!dropdown) {
        return;
      }

      const rect = item.getBoundingClientRect();
      // The dropdown is laid out at all times (hidden with visibility, not display), so it can be measured up front,
      // and its width is content driven so it does not change with the alignment.
      const width = dropdown.offsetWidth;
      const isRtl = getComputedStyle(item).direction === 'rtl';

      const fitsFromStartEdge = isRtl ? rect.right - width >= EDGE_MARGIN : rect.left + width <= viewportWidth - EDGE_MARGIN;

      item.classList.toggle('cn-dropdown-start', fitsFromStartEdge);
      item.classList.toggle('cn-dropdown-end', !fitsFromStartEdge);
    });
  };

  let frame = null;
  const scheduleAlign = () => {
    if (frame !== null) {
      cancelAnimationFrame(frame);
    }

    frame = requestAnimationFrame(() => {
      frame = null;
      alignDropdowns();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', alignDropdowns);
  } else {
    alignDropdowns();
  }

  // Web fonts change how wide the menu labels are, so measure again once they have settled.
  document.fonts?.ready.then(alignDropdowns);

  window.addEventListener('resize', scheduleAlign);
  document.addEventListener('shopify:section:load', scheduleAlign);
})();

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * <cn-carousel> — scroll-snap slider with round prev/next buttons.
 *
 * Shared by "Custom | Media carousel" and "Custom | Image strip" (slider layout). The arrows hide themselves when every
 * item already fits on screen, so the same markup works on any breakpoint and any number of items.
 *
 * Expected markup:
 *   <cn-carousel>
 *     <div data-carousel-viewport><ul><li class="cn-carousel__item">…<div data-carousel-media>…</div></li></ul></div>
 *     <button data-carousel-prev> <button data-carousel-next>
 *   </cn-carousel>
 * ---------------------------------------------------------------------------------------------------------------------
 */
(() => {
  if (customElements.get('cn-carousel')) {
    return;
  }

  class CnCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');

      if (!this.viewport) {
        return;
      }

      this.onScroll = this.update.bind(this);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

      this.prevButton?.addEventListener('click', () => this.scrollByPage(-1));
      this.nextButton?.addEventListener('click', () => this.scrollByPage(1));

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.onScroll);
      }

      // Theme editor: bring the selected block into view.
      if (window.Shopify && window.Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          event.target.scrollIntoView({ inline: 'center', block: 'nearest', behavior: event.detail.load ? 'auto' : 'smooth' });
        });
      }

      this.update();
    }

    disconnectedCallback() {
      this.viewport?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.resizeObserver?.disconnect();
    }

    get isRtl() {
      return getComputedStyle(this).direction === 'rtl';
    }

    scrollByPage(direction) {
      const item = this.viewport.querySelector('.cn-carousel__item');
      const gap = parseFloat(getComputedStyle(this.viewport.firstElementChild || this.viewport).columnGap) || 0;
      const step = item ? item.getBoundingClientRect().width + gap : this.viewport.clientWidth;

      this.viewport.scrollBy({ left: step * direction * (this.isRtl ? -1 : 1), behavior: 'smooth' });
    }

    update() {
      // Center the arrows on the image, not on the whole card (which also holds the label).
      const media = this.querySelector('[data-carousel-media]');

      if (media && media.offsetHeight > 0) {
        this.style.setProperty('--cn-carousel-arrow-top', media.offsetHeight / 2 + 'px');
      }

      if (!this.prevButton && !this.nextButton) {
        return;
      }

      const { scrollWidth, clientWidth } = this.viewport;
      const overflows = scrollWidth - clientWidth > 2;
      const position = Math.abs(this.viewport.scrollLeft);

      [this.prevButton, this.nextButton].forEach((button) => {
        if (button) {
          button.hidden = !overflows;
        }
      });

      if (this.prevButton) {
        this.prevButton.disabled = position <= 2;
      }

      if (this.nextButton) {
        this.nextButton.disabled = position + clientWidth >= scrollWidth - 2;
      }
    }
  }

  customElements.define('cn-carousel', CnCarousel);
})();

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * <cn-compare> — before / after slider driven by the cursor
 *
 * Used by "Custom | Before after". The handle follows the pointer while it is over the image rather than waiting to be
 * dragged, and stays where it was left once the pointer goes — sliding back on its own would undo the comparison the
 * visitor had just set up. start-position only decides where the handle sits before anyone has touched it.
 *
 * Position travels as a percentage in --cn-compare-position, which the stylesheet turns into a clip-path on the top
 * layer and a left offset on the handle. Nothing is measured or written per frame beyond that one custom property.
 *
 * Expected markup:
 *   <cn-compare start-position="50">
 *     <div class="cn-compare__layer">            lower image, gives the slider its height
 *     <div class="cn-compare__layer--top">       upper image, clipped
 *     <div data-compare-handle role="slider">    divider, and the keyboard control
 *   </cn-compare>
 * ---------------------------------------------------------------------------------------------------------------------
 */
(() => {
  if (customElements.get('cn-compare')) {
    return;
  }

  const KEY_STEP = 5;

  class CnCompare extends HTMLElement {
    connectedCallback() {
      this.handle = this.querySelector('[data-compare-handle]');

      this.onPointerMove = this.onPointerMove.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      this.addEventListener('pointermove', this.onPointerMove);
      this.handle?.addEventListener('keydown', this.onKeyDown);

      this.setPosition(this.clamp(parseFloat(this.getAttribute('start-position'))));
    }

    disconnectedCallback() {
      this.removeEventListener('pointermove', this.onPointerMove);
      this.handle?.removeEventListener('keydown', this.onKeyDown);
    }

    clamp(value) {
      return Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 50;
    }

    setPosition(percent) {
      this.position = this.clamp(percent);
      this.style.setProperty('--cn-compare-position', `${this.position}%`);
      this.handle?.setAttribute('aria-valuenow', Math.round(this.position));
    }

    onPointerMove(event) {
      const rect = this.getBoundingClientRect();

      if (rect.width === 0) {
        return;
      }

      this.setPosition(((event.clientX - rect.left) / rect.width) * 100);
    }

    onKeyDown(event) {
      const step = { ArrowLeft: -KEY_STEP, ArrowRight: KEY_STEP, Home: -100, End: 100 }[event.key];

      if (step === undefined) {
        return;
      }

      event.preventDefault();
      this.setPosition(this.position + step);
    }
  }

  customElements.define('cn-compare', CnCompare);
})();

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * <cn-infinite-scroll> — autoloads the next page of a paginated product grid
 *
 * Replaces the theme's numbered pagination on the collection page (see snippets/infinite-scroll.liquid). The element
 * is rendered right after the product grid, holding the URL of the next page. As soon as it scrolls into view it
 * fetches that page, appends its products to the grid, then swaps itself for the trigger rendered inside that
 * response — which already points at page N+1 — so the chain keeps going on its own. A response with no trigger
 * inside it means there is no page left, so the element just removes itself.
 *
 * Filter and sort changes are handled separately by the theme's own <product-facet>, which re-renders the whole grid
 * from page 1, so there is nothing for this element to reconcile with there.
 * ---------------------------------------------------------------------------------------------------------------------
 */
(() => {
  if (customElements.get('cn-infinite-scroll')) {
    return;
  }

  const PRODUCT_LIST_SELECTOR = '#facet-main .product-list__inner';

  class CnInfiniteScroll extends HTMLElement {
    connectedCallback() {
      if (!('IntersectionObserver' in window)) {
        return;
      }

      this.observer = new IntersectionObserver((entries) => entries[0].isIntersecting && this.load(), {
        rootMargin: '200px 0px',
      });

      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load() {
      if (this.loading) {
        return;
      }

      this.loading = true;
      this.observer.disconnect();
      this.classList.add('cn-infinite-scroll--loading');

      try {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`cn-infinite-scroll: failed to fetch ${url} (${response.status})`);
        }

        const fragment = document.createElement('div');
        fragment.innerHTML = await response.text();

        const currentList = document.querySelector(PRODUCT_LIST_SELECTOR);
        const nextItems = fragment.querySelector(PRODUCT_LIST_SELECTOR);

        if (currentList && nextItems) {
          const previousItemCount = currentList.children.length;
          currentList.insertAdjacentHTML('beforeend', nextItems.innerHTML);

          // The theme's own entrance animation ([reveal] { opacity: 0 } in theme.css) only ever plays for the
          // batch of <product-item> elements <product-list> captured when it first connected, so products appended
          // here afterwards would otherwise stay invisible forever. Reveal them outright instead of animating them.
          Array.from(currentList.children)
            .slice(previousItemCount)
            .forEach((item) => item.style.opacity = '1');
        }

        const next = fragment.querySelector('cn-infinite-scroll');
        this.replaceWith(...(next ? [next] : []));
      } catch (error) {
        // Leave the trigger in place and let the visitor retry by scrolling it back into view.
        this.classList.remove('cn-infinite-scroll--loading');
        this.loading = false;
        this.observer.observe(this);
      }
    }
  }

  customElements.define('cn-infinite-scroll', CnInfiniteScroll);
})();

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * Logo list — carousel: drag to scroll, optional auto-slide
 *
 * This site's carousel has no prev/next buttons, so there is no other way to move it with a mouse. This adds plain
 * click-and-drag scrolling straight off ".logo-list__list" (touch is left alone — it already scrolls the list
 * natively, since custom-sections.css makes it a real overflow-x: auto element) plus an optional auto-slide timer,
 * off unless a merchant turns it on via the section's "Auto slide" / "Auto slide interval" settings.
 * ---------------------------------------------------------------------------------------------------------------------
 */
(() => {
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setupCarousel = (logoList) => {
    if (logoList.dataset.cnCarouselReady === 'true') {
      return;
    }

    logoList.dataset.cnCarouselReady = 'true';

    const list = logoList.querySelector('.logo-list__list');

    if (!list) {
      return;
    }

    // Drag to scroll — mouse only, touch already scrolls the list on its own.
    let isDragging = false;
    let didDrag = false;
    let startX = 0;
    let startScrollLeft = 0;

    list.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse') {
        return;
      }

      isDragging = true;
      didDrag = false;
      startX = event.clientX;
      startScrollLeft = list.scrollLeft;
      list.classList.add('is-dragging');
      list.setPointerCapture(event.pointerId);
    });

    list.addEventListener('pointermove', (event) => {
      if (!isDragging) {
        return;
      }

      const delta = event.clientX - startX;

      if (Math.abs(delta) > 3) {
        didDrag = true;
      }

      list.scrollLeft = startScrollLeft - delta;
    });

    const endDrag = () => {
      isDragging = false;
      list.classList.remove('is-dragging');
    };

    list.addEventListener('pointerup', endDrag);
    list.addEventListener('pointercancel', endDrag);

    // A drag that actually moved the list should not also fire the logo link underneath it.
    list.addEventListener('click', (event) => {
      if (didDrag) {
        event.preventDefault();
        didDrag = false;
      }
    }, true);

    // Optional auto-slide, off unless the section setting turns it on.
    if (logoList.dataset.autoSlide !== 'true' || prefersReducedMotion()) {
      return;
    }

    const intervalMs = (parseFloat(logoList.dataset.autoSlideInterval) || 4) * 1000;
    let timer = null;

    const step = () => {
      const atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;

      if (atEnd) {
        list.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }

      const item = list.firstElementChild;
      const itemWidth = item ? item.getBoundingClientRect().width : list.clientWidth;
      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;

      list.scrollBy({ left: itemWidth + gap, behavior: 'smooth' });
    };

    const start = () => {
      if (timer === null) {
        timer = setInterval(step, intervalMs);
      }
    };

    const stop = () => {
      clearInterval(timer);
      timer = null;
    };

    list.addEventListener('mouseenter', stop);
    list.addEventListener('mouseleave', start);
    list.addEventListener('focusin', stop);
    list.addEventListener('focusout', start);
    list.addEventListener('pointerdown', stop);
    list.addEventListener('pointerup', start);

    start();
  };

  const setupAll = (root = document) => {
    root.querySelectorAll('.logo-list--carousel').forEach(setupCarousel);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setupAll());
  } else {
    setupAll();
  }

  // Theme editor: a section that gets re-rendered needs its carousel wired up again.
  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
})();
